<template>
  <section id="ai-consulting" class="h25-section">
    <div class="h25-container">
      <div class="h25-sec-hd center">
        <div class="label">AI Consulting</div>
        <h2>AI 售前咨询助手</h2>
        <p>基于企业专属知识库，7×24小时智能解答产品方案、技术架构、合作模式等问题</p>
      </div>

      <!-- 功能亮点 -->
      <div class="ai-features">
        <div class="ai-feature-card" v-for="f in features" :key="f.icon">
          <div class="fi-icon">{{ f.icon }}</div>
          <h4>{{ f.title }}</h4>
          <p>{{ f.desc }}</p>
        </div>
      </div>

      <!-- CTA 按钮 -->
      <div class="ai-cta">
        <span class="h25-btn-primary btn-lg" @click="openModal">
          🤖 立即体验 AI 咨询
        </span>
        <p class="ai-cta-note">无需登录，即开即用 · 内容已做脱敏处理，保障信息安全</p>
      </div>
    </div>

    <!-- AI 咨询模态框 -->
    <transition name="modal-fade">
      <div v-if="showModal" class="ai-modal-overlay" @click.self="closeModal">
        <div class="ai-modal" ref="modal">
          <!-- 头部 -->
          <div class="ai-modal-header">
            <div class="ai-modal-title">
              <span class="ai-avatar">🤖</span>
              <div>
                <h4>创势AI售前助手</h4>
                <p class="ai-status" :class="{ online: connected }">
                  {{ connected ? '在线 · 随时为您服务' : '连接中...' }}
                </p>
              </div>
            </div>
            <button class="ai-close-btn" @click="closeModal">✕</button>
          </div>

          <!-- 消息区 -->
          <div class="ai-chat-body" ref="chatBody">
            <div v-for="(msg, i) in messages" :key="i" class="ai-msg-row" :class="msg.role">
              <div class="ai-msg-avatar">{{ msg.role === 'ai' ? '🤖' : '👤' }}</div>
              <div class="ai-msg-bubble">
                <div class="ai-msg-name">{{ msg.role === 'ai' ? 'AI助手' : '我' }}</div>
                <div class="ai-msg-content" v-html="msg.content"></div>
                <div v-if="msg.desensitized" class="ai-desensitized-badge">已脱敏</div>
              </div>
            </div>

            <!-- 加载中 -->
            <div v-if="loading" class="ai-msg-row ai">
              <div class="ai-msg-avatar">🤖</div>
              <div class="ai-msg-bubble">
                <div class="ai-msg-name">AI助手</div>
                <div class="ai-typing"><span></span><span></span><span></span></div>
              </div>
            </div>
          </div>

          <!-- 快捷问题 -->
          <div v-if="messages.length <= 1" class="ai-quick-questions">
            <p class="ai-qq-label">💡 试试这些问题：</p>
            <div class="ai-qq-grid">
              <button v-for="q in quickQuestions" :key="q" class="ai-qq-btn" @click="sendQuickQuestion(q)">{{ q }}</button>
            </div>
          </div>

          <!-- 输入区 -->
          <div class="ai-input-bar">
            <textarea
              v-model="userInput"
              class="ai-input"
              placeholder="请输入您想咨询的问题..."
              @keydown.enter.prevent="sendMessage"
              :disabled="loading"
              rows="1"
            ></textarea>
            <button class="ai-send-btn" @click="sendMessage" :disabled="!userInput.trim() || loading">
              发送
            </button>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
// 数据脱敏函数
function desensitize(text) {
  if (!text) return text
  let result = text

  // 1. 替换项目金额（如 600多万元, 300万, 54万元等）
  result = result.replace(/(\d+)([万多]?)(元)/g, (match) => {
    return '***' + match.replace(/\d+/g, '')
  })

  // 2. 替换具体政府部门名称（如 "龙岗区大数据中心" → "***区大数据中心"）
  result = result.replace(/([^\s，。；：、！？）\)]*?)(区\w*[中心局办部委院处科所站队组])(?=[^\w]|$)/g, (match) => {
    const dept = match.replace(/^.+?(区)/, '***')
    return dept
  })

  // 3. 替换涉密/敏感客户全称（如 "深圳龙华区企业服务协会" → "***企业服务协会"）
  // 匹配 "深圳" 或 "深圳市" 开头的机构名称
  result = result.replace(/(深圳[市的]?)([一-龥]{2,}(?:公司|集团|厂|协会|中心|院|所|局|办))/g, (match, prefix, org) => {
    return '***' + org
  })

  // 4. 替换具体金额数字（独立的数字金额，如 200+, 800+ 等）
  result = result.replace(/(\d+)([+%以上余])/g, (match, num, suffix) => {
    return '***' + suffix
  })

  // 5. 替换明确的个人手机号/电话
  result = result.replace(/(1[3-9]\d)\d{4}(\d{4})/g, '$1****$2')

  return result
}

export default {
  name: 'AiConsultingSection',
  inject: ['goAnchor'],
  data() {
    return {
      showModal: false,
      connected: false,
      loading: false,
      userInput: '',
      messages: [
        {
          role: 'ai',
          content: '您好！我是创势AI售前助手，可以为您解答关于产品方案、技术架构、合作模式、落地案例等各类问题。请问有什么可以帮您的？',
          desensitized: false
        }
      ],
      features: [
        { icon: '🧠', title: 'AI 专家工作台', desc: '215个AI专家即插即用，覆盖政务、园区、制造等10+业务领域' },
        { icon: '🤖', title: '多智能体协作', desc: '多个AI智能体动态编排与协同调度，打破单点AI应用孤岛' },
        { icon: '🎙️', title: 'AI 语音服务', desc: 'ASR语音识别+TTS语音合成，多语言多方言自然交互' },
        { icon: '🔒', title: '数据安全脱敏', desc: '所有输出内容自动过滤敏感信息，保障政务与商业数据安全' }
      ],
      quickQuestions: [
        '创势互联有哪些核心AI产品？',
        '智能体协作平台能解决什么问题？',
        'AI 专家工作台有哪些核心功能？',
        'AI MCP 服务是什么？'
      ]
    }
  },
  methods: {
    openModal() {
      this.showModal = true
      this.connected = true
      this.$nextTick(() => {
        this.scrollToBottom()
        if (this.$refs.modal) {
          this.$refs.modal.querySelector('.ai-input')?.focus()
        }
      })
    },
    closeModal() {
      this.showModal = false
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const body = this.$refs.chatBody
        if (body) body.scrollTop = body.scrollHeight
      })
    },
    sendQuickQuestion(q) {
      this.userInput = q
      this.sendMessage()
    },
    async sendMessage() {
      const text = this.userInput.trim()
      if (!text || this.loading) return

      // 添加用户消息
      this.messages.push({
        role: 'user',
        content: text
      })
      this.userInput = ''
      this.loading = true
      this.scrollToBottom()

      try {
        // 调用售前汇报智能体 API（接口地址待确认）
        const response = await this.callAiApi(text)

        // 脱敏处理
        const desensitizedContent = desensitize(response)
        const wasDesensitized = desensitizedContent !== response

        this.messages.push({
          role: 'ai',
          content: desensitizedContent,
          desensitized: wasDesensitized
        })
      } catch (e) {
        this.messages.push({
          role: 'ai',
          content: '抱歉，服务暂时不可用，请稍后再试。如果问题持续存在，请联系技术支持。',
          desensitized: false
        })
      } finally {
        this.loading = false
        this.scrollToBottom()
      }
    },
    async callAiApi(question) {
      // ==========================================
      // TODO: 接入内部售前汇报智能体 API
      // 接口地址: _______________
      // 请求方式: POST
      // 请求体: { question: "..." }
      // 响应体: { answer: "..." }
      // ==========================================

      // 演示模式 - 返回模拟回复
      // 后续替换为真实API调用
      await new Promise(r => setTimeout(r, 1500))

      const demoReplies = {
        '创势互联有哪些核心AI产品？': `创势互联的核心AI产品体系包括：

1. **AI专家工作台** - 内置215个AI专家角色，覆盖政府、园区、制造等10+业务领域，即插即用
2. **智能体协作平台** - 多AI智能体动态编排与协同调度，打破单点AI应用孤岛
3. **AI售前咨询助手** - 专为企业售前打造的AI专家，支持自定义知识库
4. **AI MCP服务平台** - 标准化的AI工具集成接口，打通AI与业务系统连接
5. **AI语音交互服务** - 支持ASR语音识别+TTS语音合成的多模态交互
6. **智能体运行数据平台** - AI工作效率可视化监控与ROI分析

以上产品均支持私有化部署和云端SaaS两种模式，可根据客户需求灵活选择。`,
        'AI 专家工作台有哪些核心功能？': `**AI 专家工作台** 是创势互联核心产品之一，主要功能包括：

**🔹 215+ AI专家角色库**
- 覆盖政府、园区、制造、零售等10+业务领域
- 每个AI专家具备独立的知识体系与推理能力
- 即插即用，无需从零训练模型

**🔹 个性化定制**
- 支持企业自定义AI专家的知识库与应答逻辑
- 可根据业务流程配置AI专家的工作流
- 支持私有化知识库导入

**🔹 团队协作**
- 多个AI专家可组成虚拟团队协同工作
- 支持头脑风暴、方案推演、模拟评审等场景
- AI专家间可自动流转任务与共享上下文

**🔹 智能监控**
- 实时监控AI专家的响应质量与工作效率
- 可视化数据分析报表
- 异常行为自动告警

部署方式：支持私有化部署和SaaS云服务两种模式，保障数据安全。`,
        'AI MCP 服务是什么？': `**AI MCP 服务**（Model Context Protocol Service）是创势互联推出的AI工具集成标准协议平台：

**🔹 什么是MCP？**
MCP（模型上下文协议）是一种标准化的AI工具集成接口协议，让AI专家能够调用各类业务系统和工具来完成复杂任务。

**🔹 核心能力**
- **标准化集成** - 提供统一的API规范，降低AI与业务系统的对接成本
- **工具超市** - 预置100+业务工具适配器，覆盖OA、ERP、CRM等常见系统
- **自定义扩展** - 支持企业开发自定义工具适配器，按需接入专属业务系统
- **安全管控** - 细粒度的权限控制与调用审计，保障业务数据安全

**🔹 应用场景**
- AI专家自动查询业务数据库生成报表
- AI助手调用审批系统发起流程
- 智能体通过MCP获取实时物联网设备数据
- 多工具编排完成复杂业务流程自动化

**🔹 技术优势**
- 基于开放标准协议，非私有锁定
- 工具调用延迟<200ms，满足实时交互需求
- 支持同步调用与异步回调两种模式`,
        '生态合作伙伴有哪些权益？': `创势互联生态合作伙伴权益体系包括以下四个层级：

**🏛️ 商协会组织**
- 获得可落地的AI产品库+会员增值方案
- 销售分成+年费变现
- 将号召力转化为可持续收入

**🏢 区域运营商/集成商**
- 政务级AI产品授权+联合投标资格
- 本地化培训与技术支持
- 项目分成+代理折扣，提升客单价与中标率

**📊 行业咨询公司**
- 全套技术交付能力由创势承担
- 技术分包+运维分成
- 零技术团队交付大型项目

**💬 SaaS/企微服务商**
- 开放政务AI的API/SDK快速集成
- 按调用量/订阅分成结算
- 零研发成本扩充产品线

合作咨询请联系我们获得详细合作白皮书。`,
        'AI智能体协作平台能解决什么问题？': `**AI智能体协作平台** 主要解决以下核心问题：

**🔴 当前痛点**
- 多AI应用相互独立，形成"智能孤岛"
- 不同业务线的AI能力无法协同调度
- 数据和能力难以复用，重复建设成本高

**✅ 平台能力**
- 多AI智能体的动态编排与协同调度
- 统一的智能体注册、发现、调用机制
- 跨智能体的数据互通与上下文共享
- 可视化的工作流编排界面

**📊 实际效果**
- AI智能体协同效率提升**60%**
- 业务全周期管理数据**100%** 在线贯通
- 新智能体接入周期从月级缩短至天级

该平台已在多个项目中成功落地，支持政务、园区、制造等行业的智能化升级。`,
      }

      // 返回匹配的回复或通用回复
      for (const [key, reply] of Object.entries(demoReplies)) {
        if (question.includes(key) || key.includes(question)) {
          return reply
        }
      }

      return `感谢您的提问！关于"${question}"，创势互联在相关领域拥有成熟的解决方案和丰富的落地经验。

我们的技术团队可以为您提供：
1. **方案咨询** - 根据您的业务场景定制AI解决方案
2. **产品演示** - 安排线上/线下产品功能演示
3. **案例分享** - 提供同行业真实落地案例参考
4. **POC验证** - 在您的环境中进行概念验证测试

如需进一步沟通，请留下您的联系方式，我们的售前顾问将在24小时内与您联系。`
    }
  }
}
</script>

<style scoped>
/* 功能亮点 */
.ai-features {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 44px;
}
.ai-feature-card {
  text-align: center;
  padding: 28px 14px;
  background: var(--h25-white);
  border: 1px solid var(--h25-border);
  border-radius: var(--h25-radius);
  transition: border-color .2s;
}
.ai-feature-card:hover {
  border-color: var(--h25-accent);
  transform: translateY(-2px);
}
.fi-icon { font-size: 32px; margin-bottom: 12px; }
.ai-feature-card h4 { font-size: 14px; font-weight: 700; color: var(--h25-text); margin-bottom: 6px; }
.ai-feature-card p { font-size: 12px; color: var(--h25-muted); line-height: 1.7; }

/* CTA */
.ai-cta {
  text-align: center;
  padding: 28px 0 10px;
}
.btn-lg {
  font-size: 16px !important;
  padding: 14px 40px !important;
  border-radius: 30px !important;
  box-shadow: 0 4px 20px rgba(56, 132, 255, 0.3) !important;
  transition: all .3s !important;
  cursor: pointer;
}
.btn-lg:hover {
  box-shadow: 0 8px 32px rgba(56, 132, 255, 0.45) !important;
  transform: translateY(-2px);
}
.ai-cta-note {
  margin-top: 12px;
  font-size: 12px;
  color: var(--h25-muted2);
}

/* 模态框遮罩 */
.ai-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}
.ai-modal {
  width: 620px;
  max-width: 92vw;
  height: 80vh;
  max-height: 700px;
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,.25);
}

/* 头部 */
.ai-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}
.ai-modal-title {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ai-avatar { font-size: 28px; }
.ai-modal-title h4 { font-size: 15px; font-weight: 700; color: #333; margin: 0; }
.ai-status { font-size: 11px; color: #999; margin: 2px 0 0; }
.ai-status.online { color: #52c41a; }
.ai-close-btn {
  width: 32px; height: 32px;
  border: none; background: #f5f5f5;
  border-radius: 50%;
  font-size: 14px;
  cursor: pointer;
  color: #666;
  transition: all .2s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ai-close-btn:hover { background: #e8e8e8; color: #333; }

/* 聊天区 */
.ai-chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  background: #f8f9fb;
}
.ai-msg-row {
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
}
.ai-msg-row.ai { flex-direction: row; }
.ai-msg-row.user { flex-direction: row-reverse; }
.ai-msg-avatar {
  width: 32px; height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}
.ai-msg-row.ai .ai-msg-avatar { background: #e6f7ff; }
.ai-msg-row.user .ai-msg-avatar { background: #f0f5ff; }
.ai-msg-bubble {
  max-width: 75%;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.8;
  position: relative;
}
.ai-msg-row.ai .ai-msg-bubble {
  background: #fff;
  border: 1px solid #eee;
  border-top-left-radius: 4px;
  color: #333;
}
.ai-msg-row.user .ai-msg-bubble {
  background: var(--h25-accent);
  color: #fff;
  border-top-right-radius: 4px;
}
.ai-msg-name { font-size: 11px; color: #999; margin-bottom: 4px; font-weight: 600; }
.ai-msg-row.user .ai-msg-name { color: rgba(255,255,255,.7); text-align: right; }
.ai-msg-content { white-space: pre-wrap; word-break: break-word; }
.ai-msg-content :deep(strong) { font-weight: 700; }
.ai-msg-content :deep(code) {
  background: #f0f0f0;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 12px;
}
.ai-desensitized-badge {
  display: inline-block;
  margin-top: 6px;
  padding: 2px 8px;
  background: #fff7e6;
  color: #d46b08;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}

/* 加载动画 */
.ai-typing {
  display: flex;
  gap: 4px;
  padding: 6px 0;
}
.ai-typing span {
  width: 6px; height: 6px;
  background: #ccc;
  border-radius: 50%;
  animation: typingDot 1.4s infinite;
}
.ai-typing span:nth-child(2) { animation-delay: .2s; }
.ai-typing span:nth-child(3) { animation-delay: .4s; }
@keyframes typingDot {
  0%, 60%, 100% { opacity: .3; transform: scale(.8); }
  30% { opacity: 1; transform: scale(1.2); }
}

/* 快捷问题 */
.ai-quick-questions {
  padding: 12px 24px 16px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}
.ai-qq-label { font-size: 12px; color: #999; margin-bottom: 8px; }
.ai-qq-grid { display: flex; flex-wrap: wrap; gap: 8px; }
.ai-qq-btn {
  padding: 6px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #fff;
  font-size: 12px;
  color: #666;
  cursor: pointer;
  transition: all .2s;
  white-space: nowrap;
}
.ai-qq-btn:hover {
  border-color: var(--h25-accent);
  color: var(--h25-accent);
  background: #f0f7ff;
}

/* 输入区 */
.ai-input-bar {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 14px 24px 16px;
  border-top: 1px solid #eee;
  background: #fff;
  flex-shrink: 0;
}
.ai-input {
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  font-family: inherit;
  resize: none;
  outline: none;
  max-height: 100px;
  line-height: 1.6;
  transition: border-color .2s;
}
.ai-input:focus {
  border-color: var(--h25-accent);
}
.ai-send-btn {
  padding: 10px 22px;
  background: var(--h25-accent);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all .2s;
  white-space: nowrap;
}
.ai-send-btn:hover { opacity: .85; }
.ai-send-btn:disabled { background: #ccc; cursor: not-allowed; }

/* 过渡动画 */
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity .3s ease;
}
.modal-fade-enter-active .ai-modal,
.modal-fade-leave-active .ai-modal {
  transition: transform .3s ease;
}
.modal-fade-enter, .modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter .ai-modal,
.modal-fade-leave-to .ai-modal {
  transform: scale(.95) translateY(10px);
}

/* 响应式 */
@media(max-width:768px){
  .ai-features { grid-template-columns: repeat(2, 1fr); }
  .ai-modal { height: 90vh; max-height: 90vh; width: 96vw; max-width: 96vw; }
  .ai-msg-bubble { max-width: 85%; }
}
</style>
